/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/**
 * Format the passed string.
 * @param formatSpecifier - FormatSpecifier.
 * @param parameters -- Any number of parameters.
 * @returns The formated string.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function format(formatSpecifier: string, ...parameters: any[]): string {
    if (!formatSpecifier) {
        return '';
    }

    return formatSpecifier.replace(/{(\d+)}/g, (match: string, index: number) => {
        if (index >= parameters.length) {
            return match;
        }

        const value = parameters[index];

        if (typeof value !== 'number' && !value) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        return value.toString();
    });
}

/**
 * Determines if the specified string is undefined, null, empty, or whitespace.
 * @param value - The string to examine.
 * @returns - True if the value is undefined, null, empty, or whitespace, otherwise false.
 */
export function isNullOrWhiteSpace(value: string): boolean {
    return !value || typeof value !== 'string' || !trim(value);
}

/**
 * Trims leading and trailing whitespace from the string.
 * @param value - The string to examine.
 * @returns - The trimmed string.
 */
export function trim(value: string): string {
    // Any flavor of !value (ie. null/undefined/'') should return itself.
    if (!value || typeof value !== 'string') {
        return value;
    }

    if (value.trim) {
        return value.trim();
    }

    return value.replace(/^\s+|\s+$/g, '');
}

/**
 * Determines if the specified string starts with the specified value.
 * @param value - The string to look in.
 * @param prefix - The prefix to look for.
 * @param ignoreCase - Ignore Case.
 * @returns - True if the string starts with the prefix, otherwise false.
 */
export function startsWith(value: string, prefix: string, ignoreCase: boolean = true): boolean {
    if (!value || !prefix) {
        return false;
    }

    if (ignoreCase) {
        value = value.toLocaleLowerCase();
        prefix = prefix.toLocaleLowerCase();
    }

    if (value.startsWith) {
        return value.startsWith(prefix);
    }

    return value.startsWith(prefix);
}
